import { computed, reactive, ref } from 'vue'
import { camelizeKeys } from 'humps'
import { useLoader } from './useLoader'
import { useError } from './useError'
import { useDialog } from '@/services/useDialog'
import { useYearSelector } from '@/services/useYearSelector'
import { useCurrentUser } from '@/services/useCurrentUser'
import axios from '@/axios'

const budgetsState = reactive({
  budgets: [],
})

const amountState = reactive({
  amount: 0,
})

let currentBudget = reactive({
  contentIdList: [{ id: 0, name: '交付金' }],
  amount: 0,
  paymentDate: undefined,
  remarks: '',
})

let remainingBudget = ref(0)
export const useBudgets = () => {
  const { toggleShowLoader } = useLoader()
  const { toggleShowError } = useError()
  const resetCurrentBudget = () => {
    currentBudget = {
      memberName: '',
      contentIdList: [{ id: 0, name: '交付金' }],
      amount: 0,
      paymentDate: undefined,
      remarks: '',
    }
  }

  const setCurrentBudget = async budgetId => {
    const budget = await getBudget(budgetId)
    budget.contentIdList = contentIdToArray(budget.contentId)
    currentBudget = budget
    return new Promise(resolve => resolve(true))
  }

  /**
   * 予算リストを取得する
   *
   * @param {number} year 取得する年度
   */
  const getBudgets = async (year = new Date().getFullYear()) => {
    try {
      toggleShowLoader(true)
      let res = await axios.get(`/budgets?year=${year}`)
      res = camelizeKeys(res)
      budgetsState.budgets = Array.from(res.data.budgets)
      toggleShowLoader(false)
    } catch (e) {
      toggleShowError(true)
      toggleShowLoader(false)
    }
  }
  /**
   * 予算を取得、キャメルケースに変換した予算オブジェクトを返す
   *
   * @param {number} budgetId budgets.id
   * @returns object
   */
  const getBudget = async budgetId => {
    try {
      toggleShowLoader(true)
      const res = await axios.get(`/budgets/${budgetId}`)
      const budget = res?.data?.budget ?? null
      if (budget === null) {
        throw res
      }
      const camelizedBudget = camelizeKeys(budget)
      toggleShowLoader(false)
      return camelizedBudget
    } catch (e) {
      toggleShowError(true)
      toggleShowLoader(false)
    }
  }

  /**
   * 予算を削除する
   *
   * @param {number} budgetId budgets.id
   */
  const deleteBudget = async budgetId => {
    try {
      toggleShowLoader(true)
      const res = await axios.delete(`/budgets/${budgetId}`)
      await getRemainingBudget()
      await getBudgetsAfterResponse(res)
      toggleShowLoader(false)
    } catch (e) {
      toggleShowError(true)
      toggleShowLoader(false)
    }
  }

  /**
   * 残予算を取得する
   *
   */
  const getRemainingBudget = async () => {
    try {
      toggleShowLoader(true)
      const { currentUser } = useCurrentUser()
      if (currentUser.role.rank === 1) {
        const res = await axios.get(`/budgets/remaining`)
        amountState.amount = res.data.result.amount
      }
      toggleShowLoader(false)
    } catch (e) {
      console.error(e)
      toggleShowError(true)
      toggleShowLoader(false)
    }
  }

  /**
   * レスポンスが正常な場合予算リストを取得する
   *
   * @param {object} response
   * @returns void
   */
  const getBudgetsAfterResponse = async response => {
    const statusCode = response?.data?.code ?? -1
    if (statusCode !== 0) {
      return
    }
    const { closeDialog } = useDialog()
    const { currentYear } = useYearSelector()
    await getRemainingBudget()
    await getBudgets(currentYear.value)
    closeDialog()
  }

  /**
   * contentIdを配列に変換する
   *
   * @param {number} contentId budgets.content_id
   * @returns array
   */
  const contentIdToArray = contentId => {
    if (typeof contentId !== 'number' || contentId < 0) {
      throw new Error('invalid content_id')
    }
    let name = ''
    switch (contentId) {
      case 0:
        name = '交付金'
        break
      case 1:
        name = '前期交付金'
        break
      case 2:
        name = '後期交付金'
        break
      case 3:
        name = '一部返還'
        break
      case 4:
        name = '決算利息'
        break
      default:
        throw new Error('invalid content_id')
    }
    const content = { id: contentId, name: name }
    return [content]
  }

  return {
    budgets: computed(() => budgetsState.budgets),
    amount: computed(() => amountState.amount),
    getBudgets,
    deleteBudget,
    setCurrentBudget,
    resetCurrentBudget,
    getBudgetsAfterResponse,
    getRemainingBudget,
    currentBudget,
    remainingBudget,
  }
}
